import React, { useEffect, useMemo, useState } from 'react';
import DropDownList from '../../../component/iconDropDown/iconDropDown';
import { isClosedEvTariffRow, shouldPublishNewEvTariffPrice } from './evTariffMapper';

const DEFAULT_TARIFF = {
    tariff: '',
    rate: '',
    effectiveFrom: '',
    operator: '',
    status: 'ACTIVE',
};

const TARIFF_PRESETS = [
    'DC_FAST',
    'DC_PUBLIC',
    'AC_PUBLIC',
    'AC_HOME',
    'CUSTOM',
];

const TARIFF_PRESET_OPTIONS = TARIFF_PRESETS.map((preset) => ({
    value: preset,
    label: preset === 'CUSTOM' ? 'Custom…' : preset,
}));

const STATUS_OPTIONS = [
    { value: 'ACTIVE', label: 'Active' },
    { value: 'ARCHIVED', label: 'Archived' },
];

const EvTariffModal = ({
    initialValue,
    onCancel,
    onSave,
    onDelete,
    saving = false,
    serverError = '',
}) => {
    const isEdit = Boolean(initialValue?.id);
    const isHistorical = isEdit && isClosedEvTariffRow(initialValue);
    const [form, setForm] = useState(DEFAULT_TARIFF);
    const [error, setError] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);

    const willPublishNewPrice = useMemo(() => {
        if (!isEdit || isHistorical) return false;
        const parsedRate = Number(form.rate);
        if (!Number.isFinite(parsedRate) || parsedRate <= 0) return false;
        return shouldPublishNewEvTariffPrice(initialValue, {
            rate: parsedRate,
            effectiveFrom: form.effectiveFrom,
        });
    }, [isEdit, isHistorical, initialValue, form.rate, form.effectiveFrom]);

    useEffect(() => {
        if (initialValue) {
            setForm({ ...DEFAULT_TARIFF, ...initialValue });
        } else {
            setForm(DEFAULT_TARIFF);
        }
        setError('');
        setConfirmDelete(false);
    }, [initialValue]);

    useEffect(() => {
        if (serverError) setError(serverError);
    }, [serverError]);

    const handleChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const handleSubmit = async () => {
        if (saving || isHistorical) return;
        if (!form.tariff.trim()) {
            setError('Tariff code is required.');
            return;
        }
        const parsedRate = Number(form.rate);
        if (!Number.isFinite(parsedRate) || parsedRate <= 0) {
            setError('Enter a positive RM/kWh rate.');
            return;
        }
        if (!form.effectiveFrom) {
            setError('Effective From date is required.');
            return;
        }
        if (!form.operator.trim()) {
            setError('Operator name is required.');
            return;
        }
        setError('');
        await onSave({
            ...form,
            rate: parsedRate,
            tariff: form.tariff.trim().toUpperCase(),
            operator: form.operator.trim(),
        });
    };

    const isCustomTariff = !TARIFF_PRESETS.includes(form.tariff) || form.tariff === '';

    const tariffPresetValue = isCustomTariff ? 'CUSTOM' : form.tariff;
    const selectedTariffOption = TARIFF_PRESET_OPTIONS.find(
        (option) => option.value === tariffPresetValue,
    ) || null;
    const selectedStatusOption = STATUS_OPTIONS.find(
        (option) => option.value === form.status,
    ) || STATUS_OPTIONS[0];

    const handleDeleteClick = () => {
        if (saving) return;
        setConfirmDelete(true);
        setError('');
    };

    const handleConfirmDelete = async () => {
        if (!onDelete || saving) return;
        setError('');
        await onDelete();
    };

    return (
        <div className="pr-modal-overlay" role="dialog" aria-modal="true">
            <div className="pr-modal">
                <div className="pr-modal-hdr">
                    <div className="pr-modal-title">
                        {initialValue ? 'Edit EV Tariff' : 'Add EV Tariff'}
                    </div>
                    <div className="pr-modal-sub">
                        {isHistorical
                            ? 'Historical tariff row — read-only. Use + Add EV Tariff to publish a new rate.'
                            : 'New rates auto-close the previous row for the same tariff code.'}
                    </div>
                </div>
                <div className="pr-modal-body">
                    {willPublishNewPrice && (
                        <div className="pr-modal-note">
                            Publishing a new rate or effective date creates a new row and closes the current one.
                        </div>
                    )}
                    {error && <div className="pr-modal-error">{error}</div>}
                    {confirmDelete && (
                        <div className="pr-modal-confirm">
                            <p>
                                Delete
                                {' '}
                                <strong>{form.tariff || 'this tariff'}</strong>
                                ? This archives the tariff and removes it from the current list.
                            </p>
                            <div className="pr-modal-confirm-actions">
                                <button
                                  type="button"
                                  className="pr-btn pr-btn-ghost pr-btn-sm"
                                  onClick={() => setConfirmDelete(false)}
                                  disabled={saving}
                                >
                                    Keep tariff
                                </button>
                                <button
                                  type="button"
                                  className="pr-btn pr-btn-danger pr-btn-sm"
                                  onClick={handleConfirmDelete}
                                  disabled={saving}
                                >
                                    {saving ? 'Deleting…' : 'Confirm delete'}
                                </button>
                            </div>
                        </div>
                    )}
                    <div className="pr-field">
                        <span>Tariff</span>
                        <div className="pr-modal-dropdown">
                            <DropDownList
                              placeholder="Select tariff"
                              selectedValue={selectedTariffOption}
                              setSelectedValue={(option) => {
                                  const value = option?.value ?? 'CUSTOM';
                                  handleChange('tariff', value === 'CUSTOM' ? '' : value);
                              }}
                              options={TARIFF_PRESET_OPTIONS}
                              width="100%"
                              controlHeight={44}
                              menuPortal
                              isDisabled={isEdit || isHistorical}
                            />
                        </div>
                        {isCustomTariff && (
                            <input
                              className="pr-input"
                              placeholder="Custom tariff code"
                              value={form.tariff}
                              onChange={(e) => handleChange('tariff', e.target.value)}
                              aria-label="Custom tariff code"
                              disabled={isEdit || isHistorical}
                            />
                        )}
                    </div>
                    <label className="pr-field" htmlFor="pr-rate">
                        <span>RM / kWh</span>
                        <input
                          id="pr-rate"
                          className="pr-input"
                          type="number"
                          min="0"
                          step="0.01"
                          value={form.rate}
                          onChange={(e) => handleChange('rate', e.target.value)}
                          disabled={isHistorical}
                        />
                    </label>
                    <label className="pr-field" htmlFor="pr-eff">
                        <span>Effective From</span>
                        <input
                          id="pr-eff"
                          className="pr-input"
                          type="date"
                          value={form.effectiveFrom}
                          onChange={(e) => handleChange('effectiveFrom', e.target.value)}
                          disabled={isHistorical}
                        />
                    </label>
                    <label className="pr-field" htmlFor="pr-operator">
                        <span>Operator</span>
                        <input
                          id="pr-operator"
                          className="pr-input"
                          type="text"
                          placeholder="Gentari, JomCharge, TNB Residential…"
                          value={form.operator}
                          onChange={(e) => handleChange('operator', e.target.value)}
                          disabled={isHistorical}
                        />
                    </label>
                    <div className="pr-field">
                        <span>Status</span>
                        <div className="pr-modal-dropdown">
                            <DropDownList
                              placeholder="Select status"
                              selectedValue={selectedStatusOption}
                              setSelectedValue={(option) => handleChange('status', option?.value ?? 'ACTIVE')}
                              options={STATUS_OPTIONS}
                              width="100%"
                              controlHeight={44}
                              menuPortal
                              isDisabled={isHistorical}
                            />
                        </div>
                    </div>
                </div>
                <div className="pr-modal-footer">
                    {isEdit && onDelete && !confirmDelete && !isHistorical && (
                        <button
                          type="button"
                          className="pr-btn pr-btn-danger pr-btn-sm"
                          onClick={handleDeleteClick}
                          disabled={saving}
                        >
                            Delete
                        </button>
                    )}
                    <div className="pr-modal-footer-actions">
                        <button type="button" className="pr-btn pr-btn-ghost pr-btn-sm" onClick={onCancel} disabled={saving}>Cancel</button>
                        {!isHistorical && (
                            <button type="button" className="pr-btn pr-btn-primary pr-btn-sm" onClick={handleSubmit} disabled={saving}>
                                {saving
                                    ? 'Saving…'
                                    : (willPublishNewPrice ? 'Publish new rate' : (isEdit ? 'Save changes' : 'Add tariff'))}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EvTariffModal;
